import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ActiveClassNavLink from "../../routing/ActiveClassNavLink";

import {
  subscriptionValues,
  useGetUserSubscriptionTier,
} from "../../customHooks/useGetUserSubscriptionTier";
import { useGetLimits } from "../../customHooks/useGetLimits";

import { ROUTES } from "../../_constant/screens";

import FaceIcon from "@mui/icons-material/Face";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import EventIcon from "@mui/icons-material/Event";

import { Drawer, IconButton, List, ListItemIcon, ListItemText, Toolbar, useTheme } from "@mui/material";
import Hidden from "../Hidden/Hidden";
import Divider from "@mui/material/Divider";

import "./SettingsDrawer.scss";

import ListItemButton from "@mui/material/ListItemButton";

export interface PopoverLinkItem {
  name: string;
  url: string;
  icon?: any;
  disabled?: boolean;
}
interface getSettingsLinksParams {
  profile?: boolean;
  accounts?: boolean;
  myBusiness?: boolean;
  calendarFeeds?: boolean;
  notifications?: boolean;
  about?: boolean;
}

export const getSettingsLinks = ({
  profile = false,
  accounts = false,
  myBusiness = true,
  calendarFeeds = true,
  notifications = false,
  about = false,
}: getSettingsLinksParams): PopoverLinkItem[] => {
  return [
    {
      name: "Profile",
      url: ROUTES.EDIT_PROFILE,
      icon: <FaceIcon />,
      disabled: profile,
    },
    {
      name: "Accounts",
      url: ROUTES.ACCOUNTS,
      icon: <PeopleAltOutlinedIcon />,
      disabled: accounts,
    },
    {
      name: "My Business",
      url: ROUTES.BUSINESS,
      icon: <BusinessCenterOutlinedIcon />,
      disabled: myBusiness,
    },
    {
      name: "Calendar Feeds",
      url: ROUTES.CALENDAR_FEEDS,
      icon: <EventIcon />,
      disabled: calendarFeeds,
    },
    {
      name: "Notifications",
      url: ROUTES.NOTIFICATIONS,
      icon: <NotificationsNoneIcon />,
      disabled: notifications,
    },
    {
      name: "About",
      url: ROUTES.ABOUT,
      icon: <BookOutlinedIcon />,
      disabled: about,
    },
  ];
};

const SettingsDrawer: React.FC = (props: any) => {
  const theme = useTheme();

  const selectedSx = {
    "&.Mui-selected": {
      "& > *": {
        color: theme.palette.primary.main,
      },
    },
  };

  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const limits = useGetLimits();
  const enableCalendarFeed = limits?.user.calendarIntegration.Value;

  const userSubscriptionTier = useGetUserSubscriptionTier();
  const enableBusinessAccounts = userSubscriptionTier.value === subscriptionValues.professional;

  const links: PopoverLinkItem[] = getSettingsLinks({
    myBusiness: !enableBusinessAccounts,
    calendarFeeds: !enableCalendarFeed,
  });

  const handleLogout = () => {
    navigate(ROUTES.LOGOUT);
  };

  const drawer = (
    <List className="SettingsDrawer__list" disablePadding>
      {links
        .filter((el) => !el.disabled)
        .map(({ name, url, icon }) => (
          <ListItemButton
            disableGutters
            component={ActiveClassNavLink}
            to={url}
            key={name}
            activeClassName="Mui-selected"
            sx={selectedSx}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={name} />
          </ListItemButton>
        ))}
      <Divider />
      <ListItemButton disableGutters onClick={handleLogout}>
        <ListItemIcon>
          <ExitToAppOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="Logout" />
      </ListItemButton>
    </List>
  );

  return (
    <Hidden smDown implementation="css">
      <Drawer
        variant="temporary"
        anchor={theme.direction === "rtl" ? "right" : "left"}
        open={mobileOpen}
        onClose={() => setMobileOpen(!mobileOpen)}
      >
        <Toolbar className="SettingsDrawer__toolBar" disableGutters>
          <IconButton onClick={() => setMobileOpen(!mobileOpen)} size="large">
            <MenuOpenOutlinedIcon />
          </IconButton>
        </Toolbar>
        {drawer}
      </Drawer>
      <Drawer variant="permanent" open className="SettingsDrawer">
        <Toolbar className="SettingsDrawer__toolBar" disableGutters>
          <IconButton onClick={() => setMobileOpen(!mobileOpen)} size="large">
            <MenuOutlinedIcon />
          </IconButton>
        </Toolbar>
        {drawer}
      </Drawer>
    </Hidden>
  );
};

export default SettingsDrawer;
